import React from "react";
import { TrendingUp, Users2, SlidersHorizontal, FileSpreadsheet, ShieldCheck, Wallet } from "lucide-react";
import Seo, { breadcrumbJsonLd } from "@/components/Seo";
import PageHero from "@/components/ui-kit/PageHero";
import { Container, Section, Eyebrow, Badge } from "@/components/ui-kit/Section";
import { Reveal, RevealGroup, RevealItem } from "@/components/motion/Reveal";
import CommissionCalculator from "@/components/calculators/CommissionCalculator";
import AgencyLeadForm from "@/components/forms/AgencyLeadForm";
import FinalCta from "@/components/blocks/FinalCta";
import Cta from "@/components/ui-kit/Cta";
import { IMAGERY } from "@/lib/site";

const FEATURES = [
  {
    icon: Users2,
    title: "Every client in one dashboard",
    body: "See each client's set asides, routed amounts, and status side by side, without logging in and out of separate accounts.",
  },
  {
    icon: SlidersHorizontal,
    title: "Adjust rates within consent guardrails",
    body: "Change a client's percentage when their situation changes. Clients are always notified, and their acknowledgment is recorded before it takes effect.",
  },
  {
    icon: FileSpreadsheet,
    title: "Per client and consolidated reporting",
    body: "Export what you need as CSV or PDF, for a single client or across your whole book, ready for quarterly and annual filing.",
  },
  {
    icon: ShieldCheck,
    title: "Client data isolated to your agency",
    body: "Each agency sees only its own clients. Data is separated so nothing leaks between firms.",
  },
];

export default function Agencies() {
  return (
    <>
      <Seo
        title="ETAP for Agencies | Earn 0.25% on every routed transaction"
        description="Manage every client's estimated tax set asides in one dashboard and earn 0.25 percent on each routed transaction. Book a walkthrough with the ETAP agency team."
        path="/agencies"
        jsonLd={[
          breadcrumbJsonLd([
            { name: "Home", path: "/" },
            { name: "For Agencies", path: "/agencies" },
          ]),
        ]}
      />

      <PageHero
        eyebrow="For agencies and accountants"
        title="Earn on every transaction your clients route"
        subtitle="For an agency referred user, the flat 0.5 percent fee splits evenly: 0.25 percent to you, 0.25 percent to the platform. Your clients still pay 0.5 percent total, never more."
      >
        <div className="mt-8 flex flex-wrap gap-3">
          <Cta to="#book" variant="primary" size="lg" location="agencies-hero" withArrow testId="agencies-hero-cta">
            Book a Walkthrough
          </Cta>
          <Cta to="/get-started" variant="outline" size="lg" location="agencies-hero-start" testId="agencies-hero-start">
            Start as an Agency
          </Cta>
        </div>
      </PageHero>

      {/* Earnings model */}
      <Section className="bg-canvas pt-4">
        <Container>
          <div className="grid items-center gap-10 lg:grid-cols-12">
            <div className="lg:col-span-6">
              <Reveal>
                <Eyebrow>The earnings model</Eyebrow>
                <h2 className="mt-4 font-display text-[clamp(1.75rem,3.6vw,2.75rem)] font-bold leading-tight tracking-tight text-etap-deep-900">
                  A commission that reconciles to real transactions
                </h2>
                <p className="mt-4 text-lg text-etap-deep-600">
                  Your earnings are paid on a schedule and shown in an earnings dashboard that ties back to the
                  transaction logs. There is nothing to estimate and nothing hidden. What your clients route is what your
                  commission is based on.
                </p>
              </Reveal>
            </div>
            <div className="lg:col-span-6">
              <Reveal delay={0.1}>
                <div className="rounded-3xl border border-etap-deep-100 bg-white p-7 shadow-etap-lg">
                  <p className="text-sm font-semibold text-etap-deep-600">On a $1,000 routed amount</p>
                  <div className="mt-5 space-y-3">
                    <div className="flex items-center justify-between rounded-2xl border border-etap-mint-300 bg-etap-mint-100 p-4">
                      <span className="flex items-center gap-3 font-display font-bold text-etap-deep-800">
                        <Wallet className="h-5 w-5 text-etap-deep" aria-hidden="true" /> Your agency (0.25%)
                      </span>
                      <span className="font-display text-lg font-bold text-etap-deep-800">$2.50</span>
                    </div>
                    <div className="flex items-center justify-between rounded-2xl border border-etap-deep-100 bg-etap-deep-50 p-4">
                      <span className="flex items-center gap-3 font-display font-bold text-etap-deep-800">
                        <TrendingUp className="h-5 w-5 text-etap-primary" aria-hidden="true" /> Platform (0.25%)
                      </span>
                      <span className="font-display text-lg font-bold text-etap-deep-800">$2.50</span>
                    </div>
                  </div>
                  <p className="mt-5 text-sm text-etap-deep-500">
                    Total fee to the client is 0.5 percent, or $5.00. It never goes higher.
                  </p>
                </div>
              </Reveal>
            </div>
          </div>
        </Container>
      </Section>

      {/* Features */}
      <Section className="bg-white">
        <Container>
          <div className="max-w-2xl">
            <Reveal>
              <Eyebrow>What you manage</Eyebrow>
              <h2 className="mt-4 font-display text-[clamp(2rem,4vw,3rem)] font-bold leading-tight tracking-tight text-etap-deep-900">
                Oversight for your whole book, without the busywork
              </h2>
            </Reveal>
          </div>
          <RevealGroup className="mt-12 grid gap-6 md:grid-cols-2">
            {FEATURES.map((f) => (
              <RevealItem
                key={f.title}
                className="rounded-3xl border border-etap-deep-100 bg-canvas p-8 shadow-etap-sm transition-shadow duration-300 hover:shadow-etap-lg"
              >
                <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-etap-deep text-white">
                  <f.icon className="h-6 w-6" aria-hidden="true" />
                </span>
                <h3 className="mt-6 font-display text-xl font-bold tracking-tight text-etap-deep-800">{f.title}</h3>
                <p className="mt-3 text-etap-deep-600">{f.body}</p>
              </RevealItem>
            ))}
          </RevealGroup>
        </Container>
      </Section>

      {/* Commission calculator */}
      <Section className="bg-canvas">
        <Container>
          <div className="grid gap-10 lg:grid-cols-12 lg:items-center">
            <div className="lg:col-span-5">
              <Reveal>
                <Badge>Estimate your earnings</Badge>
                <h2 className="mt-5 font-display text-[clamp(1.75rem,3.6vw,2.75rem)] font-bold leading-tight tracking-tight text-etap-deep-900">
                  See what your book could earn
                </h2>
                <p className="mt-4 text-lg text-etap-deep-600">
                  Move the sliders to estimate monthly and annual commission from the volume your clients route. These
                  are estimates, not a promise of earnings.
                </p>
              </Reveal>
            </div>
            <div className="lg:col-span-7">
              <Reveal delay={0.1}>
                <CommissionCalculator />
              </Reveal>
            </div>
          </div>
        </Container>
      </Section>

      {/* Image + lead form */}
      <Section id="book" className="bg-white">
        <Container>
          <div className="grid gap-10 lg:grid-cols-12">
            <div className="lg:col-span-5">
              <Reveal>
                <Eyebrow>Book a walkthrough</Eyebrow>
                <h2 className="mt-4 font-display text-[clamp(1.75rem,3.6vw,2.75rem)] font-bold leading-tight tracking-tight text-etap-deep-900">
                  See it with your own client mix
                </h2>
                <p className="mt-4 text-lg text-etap-deep-600">
                  Tell us about your firm and we will walk you through onboarding, rate management, reporting, and how
                  commission payouts work.
                </p>
                <div className="relative mt-8">
                  <div
                    aria-hidden="true"
                    className="pointer-events-none absolute -inset-3 -z-10 rounded-[2rem] bg-etap-primary-100/60 blur-2xl"
                  />
                  <img
                    src={IMAGERY.agency}
                    alt="A modern accounting team working together in an office"
                    loading="lazy"
                    className="aspect-[4/3] w-full rounded-[1.5rem] border border-white object-cover shadow-etap-lg"
                  />
                </div>
              </Reveal>
            </div>
            <div className="lg:col-span-7">
              <Reveal delay={0.1}>
                <AgencyLeadForm />
              </Reveal>
            </div>
          </div>
        </Container>
      </Section>

      <FinalCta
        location="agencies-final"
        eyebrow="Ready when you are"
        title="Turn your client base into recurring commission"
        subtitle="Onboard clients, manage rates within consent guardrails, and watch earnings reconcile to every routed transaction."
        primaryLabel="Start as an Agency"
        primaryTo="/get-started"
        secondaryLabel="Book a Walkthrough"
        secondaryTo="#book"
      />
    </>
  );
}
